import { RowCell, RowsGrid } from "@don/design-system";
import type * as React from "react";

import { COLUMNS, type Column } from "./columns.js";
import { ExpiryCell, ExpiryRowDetail } from "./ExpiryCell.js";
import { markerFor, type RenewalState } from "./retention.js";
import { SummaryCell } from "./SummaryCell.js";
import { durationLabel, sessionTitle, startedLabel, type LibraryRow } from "./row.js";
import { markMatches } from "./search.js";

/** A search match in the title (design-spec §2 `--tone-2`, variant E `mark`). */
const MARK = {
  background: "var(--tone-2)",
  color: "var(--ink-1)",
  fontWeight: 600,
  borderRadius: "var(--radius-inline)",
  padding: "0 3px",
  margin: "0 -1px",
} as const;

/**
 * One session row on the Sessions grid (design-spec §4 "Session row", issue 193): title
 * (18/600 ink-1, by `sessionTitle`), person, Started and Length (right-aligned tabular),
 * Summary, Video. Every cell is one line with an ellipsis and carries its full text as
 * `title`. No snippet and no notes count (D28).
 *
 * A row is the whole click target: the owner's only entry point to a session is this list,
 * so the affordance is the row rather than the title inside it. `role="link"` plus Enter and
 * Space, because a `div` with an `onClick` is reachable by a mouse only. It is not an `<a>`:
 * a red expiry marker is a button inside the row (issue 195), and a button inside a link is
 * invalid markup.
 *
 * An open red marker adds its detail below the cells, spanning the row (`ExpiryRowDetail`).
 *
 * With a query in the search field, each occurrence of it in the title is a `<mark>`
 * (issue 194, `markMatches`); the cell's `title` attribute stays the whole title.
 */
export function LibraryRowView({
  row,
  nowMs,
  onOpen,
  onRenew,
  renewal = { kind: "idle" },
  expanded = false,
  onToggle = () => {},
  query = "",
}: {
  row: LibraryRow;
  nowMs: number;
  onOpen: (row: LibraryRow) => void;
  /** Renew this session, or `undefined` where there is nowhere to send a renewal. */
  onRenew?: (sessionId: string) => void;
  /** What the screen knows about this session's renewal (a refusal reads `Video deleted`). */
  renewal?: RenewalState;
  /** Whether this row's red marker is open. */
  expanded?: boolean;
  /** Open or close this row's red marker. */
  onToggle?: () => void;
  /** The search field's text, whose matches are marked in the title. */
  query?: string;
}) {
  const marker = markerFor(nowMs, row, renewal);
  const open = expanded && marker.level === "urgent" && marker.detail !== undefined;
  // Every cell carries `data-column`, which is what makes the header-to-row alignment
  // assertable: a test addresses the header cell and the cell beneath it by the same key.
  const text = (
    column: Column,
    value: string,
    extra: Record<string, string> = {},
    content: React.ReactNode = value,
  ) => (
    <RowCell
      key={column.key}
      data-column={column.key}
      lead={column.key === "title"}
      align={column.align}
      title={value}
      {...extra}
    >
      {content}
    </RowCell>
  );
  const cell = (column: Column) => {
    switch (column.key) {
      case "title": {
        const title = sessionTitle(row);
        const runs = markMatches(title, query).map((run, index) =>
          run.match ? (
            <mark key={index} style={MARK}>
              {run.text}
            </mark>
          ) : (
            run.text
          ),
        );
        return text(column, title, {}, runs);
      }
      case "person":
        return text(column, row.person);
      case "when":
        return text(column, startedLabel(nowMs, row.startedAtMs));
      case "duration":
        // `data-elapsed`: a length, never a clock time (issue 191). An em dash here is the
        // "no value yet" glyph, not punctuation: a session not yet ended has no length.
        return text(column, durationLabel(row) ?? "—", { "data-elapsed": "" });
      case "summary":
        return (
          <SummaryCell
            key={column.key}
            column={column}
            state={row.summaryState}
            reason={row.summaryAbsentReason}
          />
        );
      case "expiry":
        return (
          <ExpiryCell
            key={column.key}
            column={column}
            marker={marker}
            sessionId={row.sessionId}
            expanded={open}
            onToggle={onToggle}
          />
        );
    }
  };

  return (
    <RowsGrid
      kind="row"
      data-library-row={row.sessionId}
      role="link"
      tabIndex={0}
      onClick={() => onOpen(row)}
      onKeyDown={(event) => {
        // **The keyboard half of `ExpiryCell`'s `stopPropagation`.** The red marker and the
        // detail's Renew are `<button>`s inside this row: a keydown on one bubbles here, and
        // cancelling the default action on a button's keydown is what suppresses the click
        // the browser would otherwise synthesise. So without this line Enter and Space on a
        // control that acts rather than navigates would open the session instead.
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(row);
        }
      }}
    >
      {COLUMNS.map(cell)}
      {open && <ExpiryRowDetail sessionId={row.sessionId} detail={marker.detail!} onRenew={onRenew} />}
    </RowsGrid>
  );
}
